// PayrollSubmissionDialog.js
// Dialog for submitting payroll for a single employee

import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  Divider,
  Paper,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPayrollSubmission } from '../../services/employeeService';

const months = [
  { value: 1, label: 'ינואר' },
  { value: 2, label: 'פברואר' },
  { value: 3, label: 'מרץ' },
  { value: 4, label: 'אפריל' },
  { value: 5, label: 'מאי' },
  { value: 6, label: 'יוני' },
  { value: 7, label: 'יולי' },
  { value: 8, label: 'אוגוסט' },
  { value: 9, label: 'ספטמבר' },
  { value: 10, label: 'אוקטובר' },
  { value: 11, label: 'נובמבר' },
  { value: 12, label: 'דצמבר' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const PayrollSubmissionDialog = ({ open, onClose, employee }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    PeriodYear: currentYear,
    PeriodMonth: new Date().getMonth() + 1,
    HoursWorked: '',
    Deductions: '',
    Additions: '',
    DeductionNotes: '',
    AdditionNotes: '',
    Note: '',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open && employee) {
      const now = new Date();
      setFormData({
        PeriodYear: now.getFullYear(),
        PeriodMonth: now.getMonth() + 1,
        HoursWorked: '',
        Deductions: '',
        Additions: '',
        DeductionNotes: '',
        AdditionNotes: '',
        Note: '',
      });
      setError('');
    }
  }, [open, employee]);

  // Scroll to error
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const createMutation = useMutation({
    mutationFn: createPayrollSubmission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employeeMetrics'] });
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה בהגשת משכורת');
      setIsSubmitting(false);
    },
  });

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // Calculate total
  const calculateTotal = () => {
    if (!employee) return 0;

    let base = 0;
    if (employee.WageType === 'hourly' || employee.WageType === 'daily') {
      const qty = parseFloat(formData.HoursWorked) || 0;
      base = qty * parseFloat(employee.WageAmount);
    } else {
      base = parseFloat(employee.WageAmount);
    }

    const deductions = parseFloat(formData.Deductions) || 0;
    const additions = parseFloat(formData.Additions) || 0;

    return base - deductions + additions;
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    const needsQuantity = employee.WageType === 'hourly' || employee.WageType === 'daily';
    if (needsQuantity && (!formData.HoursWorked || parseFloat(formData.HoursWorked) <= 0)) {
      setError(employee.WageType === 'hourly' ? 'יש להזין שעות עבודה' : 'יש להזין ימי עבודה');
      return;
    }

    const total = calculateTotal();
    if (total <= 0) {
      setError('סכום המשכורת חייב להיות חיובי');
      return;
    }

    setIsSubmitting(true);

    const data = {
      EmployeeID: employee.EmployeeID,
      PeriodYear: parseInt(formData.PeriodYear, 10),
      PeriodMonth: parseInt(formData.PeriodMonth, 10),
      HoursWorked: (employee.WageType === 'hourly' || employee.WageType === 'daily') ? parseFloat(formData.HoursWorked) : null,
      Deductions: parseFloat(formData.Deductions) || 0,
      Additions: parseFloat(formData.Additions) || 0,
      DeductionNotes: formData.DeductionNotes.trim() || null,
      AdditionNotes: formData.AdditionNotes.trim() || null,
      Note: formData.Note.trim() || null,
    };

    createMutation.mutate(data);
  };

  if (!employee) return null;

  const total = calculateTotal();
  const isHourly = employee.WageType === 'hourly';
  const isDaily = employee.WageType === 'daily';
  const needsQuantity = isHourly || isDaily;

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        הגשת משכורת - {employee.FirstName} {employee.LastName}
      </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert ref={errorRef} severity="error">{error}</Alert>
            )}

            {/* Employee info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">פרטי עובד</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                <Typography variant="body1">סוג שכר: {isHourly ? 'שעתי' : isDaily ? 'יומי' : 'חודשי'}</Typography>
                <Typography variant="body1">
                  {isHourly ? `שכר לשעה: ₪${parseFloat(employee.WageAmount).toLocaleString()}`
                    : isDaily ? `שכר ליום: ₪${parseFloat(employee.WageAmount).toLocaleString()}`
                    : `שכר חודשי: ₪${parseFloat(employee.WageAmount).toLocaleString()}`}
                </Typography>
              </Box>
            </Paper>

            {/* Period selection */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>שנה</InputLabel>
                <Select
                  value={formData.PeriodYear}
                  onChange={handleChange('PeriodYear')}
                  label="שנה"
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>חודש</InputLabel>
                <Select
                  value={formData.PeriodMonth}
                  onChange={handleChange('PeriodMonth')}
                  label="חודש"
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Hours/Days worked (for hourly/daily employees) */}
            {needsQuantity && (
              <TextField
                label={isHourly ? 'שעות עבודה' : 'ימי עבודה'}
                type="number"
                value={formData.HoursWorked}
                onChange={handleChange('HoursWorked')}
                fullWidth
                required
                InputProps={{
                  inputProps: { min: 0, step: 0.5 },
                }}
              />
            )}

            <Divider />

            {/* Deductions */}
            <TextField
              label="ניכויים"
              type="number"
              value={formData.Deductions}
              onChange={handleChange('Deductions')}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                inputProps: { min: 0 },
              }}
            />
            <TextField
              label="פירוט ניכויים"
              value={formData.DeductionNotes}
              onChange={handleChange('DeductionNotes')}
              fullWidth
              multiline
              rows={2}
            />

            {/* Additions */}
            <TextField
              label="תוספות"
              type="number"
              value={formData.Additions}
              onChange={handleChange('Additions')}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                inputProps: { min: 0 },
              }}
            />
            <TextField
              label="פירוט תוספות"
              value={formData.AdditionNotes}
              onChange={handleChange('AdditionNotes')}
              fullWidth
              multiline
              rows={2}
            />

            {/* Note */}
            <TextField
              label="הערה"
              value={formData.Note}
              onChange={handleChange('Note')}
              fullWidth
              multiline
              rows={2}
            />

            <Divider />

            {/* Total calculation */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h6" align="center">
                סה"כ לתשלום: ₪{total.toLocaleString()}
              </Typography>
              {needsQuantity && formData.HoursWorked && (
                <Typography variant="body1" align="center" sx={{ mt: 1 }}>
                  ({parseFloat(formData.HoursWorked)} {isHourly ? 'שעות' : 'ימים'} × ₪{parseFloat(employee.WageAmount).toLocaleString()} = ₪{(parseFloat(formData.HoursWorked) * parseFloat(employee.WageAmount)).toLocaleString()})
                </Typography>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'הגש משכורת'}
          </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default PayrollSubmissionDialog;
